import { API, AuthData, clearAuthData, setAuthData } from "./api";
import { AppUser, BootstrapData, CSCoreAuthData, User } from "../lib/auth";

import { jsonToUri } from "@civicscience/chops";

/**
 * Handles 3 use cases:
 *
 * 1. Initial app load where the user is already authenticated against Auth0.
 * 2. Initial app load where the user is NOT authenticated against Auth0.
 *    In this case, we'll use the Core API to refresh the user's token.
 * 3. After explicit login (against Core API) where the caller will supply auth data.
 *
 * In all cases, we set the auth data and then fetch the initial user data.
 *
 * Note: This function is always called _after_ Auth0's lib is given a chance to
 * check if the user is authenticated via Auth0. Auth0's check is always the first
 * step in the auth flow and we won't proceed until it's done.
 */
const bootstrapApp = async (bootstrapData: BootstrapData): Promise<AppUser> => {
  const authData: AuthData =
    bootstrapData?.kind === "token-refresh"
      ? await refreshAuthTokenAsAuthData()
      : bootstrapData;

  setAuthData(authData);

  const { data: user } = await API.get<User>("/api/1/profile/");

  return user;
};

/**
 * Attempt to log in via username and password credentials, receiving a JWT token on success.
 */
const getAuthToken = (
  username: string,
  password: string,
): Promise<CSCoreAuthData> => {
  const data = jsonToUri({
    grantType: "",
    scope: "",
    clientSecret: "",
    username,
    password,
  });

  const config = {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  return API.post<CSCoreAuthData>("/api/1/auth/token", data, config).then(
    (res) => res.data,
  );
};

/**
 * Refresh the user's current JWT token via a refresh_token cookie based request.
 */
const refreshAuthToken = async (): Promise<CSCoreAuthData> => {
  const res = await API.get("/api/1/auth/token/refresh");
  return res.data;
};

/**
 * Wrapper over `refreshAuthToken` that returns the data in the `AuthData` format.
 * Basically, tags the data with the "cs-core" kind.
 */
const refreshAuthTokenAsAuthData = async (): Promise<AuthData> => {
  const authData = await refreshAuthToken();
  return { kind: "cs-core", authData };
};

/**
 * Logout the current user.
 * Additionally clears any auth data.
 */
const logout = async (): Promise<void> => {
  await API.get("/api/1/auth/logout");
  clearAuthData();
};

export default {
  getAuthToken,
  logout,
  bootstrapApp,
};
